import moment from "moment-timezone";
import {
  AccountingSystemForCalculatingFinancialStatistics as Basis,
  CompareBucket,
  CompareBy,
  ComparePeriod,
} from "client/openapi";

export type Measure = "revenue" | "cost" | "net" | "hours";
/** A range of days, both ends included, as "YYYY-MM-DD". */
export type Range = [string, string];

// Insights' colors: the revenue, cost and net lines, and the hours area.
export const MEASURES: { value: Measure; label: string; color: string }[] = [
  { value: "revenue", label: "Revenue", color: "#059669" },
  { value: "cost", label: "Cost", color: "#d97706" },
  { value: "net", label: "Net", color: "#2563eb" },
  { value: "hours", label: "Hours", color: "#7ABFFF" },
];

export const DAY = "YYYY-MM-DD";

/** The page's picked dates (Date strings) as a range of days. */
export const toRange = (start: string, end: string): Range => [
  moment(start).format(DAY),
  moment(end).format(DAY),
];

/** Whole calendar months: the first of a month through the last of one. */
function wholeMonths([start, until]: Range): boolean {
  const a = moment(start, DAY);
  const b = moment(until, DAY);
  return a.date() === 1 && b.isSame(b.clone().endOf("month"), "day");
}

/** The same dates a year earlier; whole months end on that month's last day (Feb 29). */
export function sameRangeLastYear(range: Range): Range {
  const last = moment(range[1], DAY).subtract(1, "year");
  return [
    moment(range[0], DAY).subtract(1, "year").format(DAY),
    (wholeMonths(range) ? last.endOf("month") : last).format(DAY),
  ];
}

/** The period picked: a preset counted from today, or custom dates. */
export type Period =
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year"
  | "last_6_months"
  | "custom";
export type PresetPeriod = Exclude<Period, "custom">;

export const PERIODS: { value: PresetPeriod; label: string }[] = [
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "last_quarter", label: "Last quarter" },
  { value: "this_year", label: "This year" },
  { value: "last_year", label: "Last year" },
  { value: "last_6_months", label: "Last 6 months" },
];
export const periodLabel = (period: Period) =>
  PERIODS.find((p) => p.value === period)?.label ?? "Custom";

type Unit = "week" | "month" | "quarter" | "year";
const UNIT_OF: Partial<Record<Period, Unit>> = {
  this_week: "week",
  last_week: "week",
  this_month: "month",
  last_month: "month",
  this_quarter: "quarter",
  last_quarter: "quarter",
  this_year: "year",
  last_year: "year",
};

/**
 * A preset's days for `today` (the organization's date): "This …" runs from
 * the start of the week (Sunday), month, quarter or year through today;
 * "Last …" is the whole one before; "Last 6 months" ends today.
 */
export function periodRange(period: PresetPeriod, today: string): Range {
  const day = moment(today, DAY);
  const unit = UNIT_OF[period];
  if (!unit) return [day.clone().subtract(6, "months").format(DAY), today];
  if (period.startsWith("this_")) {
    return [day.clone().startOf(unit).format(DAY), today];
  }
  const before = day.clone().subtract(1, unit);
  return [
    before.clone().startOf(unit).format(DAY),
    before.clone().endOf(unit).format(DAY),
  ];
}

/** What the period is compared with (Insights only). */
export type CompareTo = "none" | "previous_period" | "previous_year" | "custom";

/**
 * The previous period. For a calendar preset, the same span one unit back:
 * this month so far (Oct 1–6) against Sep 1–6, last month against the whole
 * month before. For custom dates or the last 6 months, the same number of
 * days just before.
 */
export function previousPeriod(period: Period, range: Range): Range {
  const [a, b] = range.map((d) => moment(d, DAY));
  const unit = UNIT_OF[period];
  if (unit) {
    const start = a.clone().subtract(1, unit);
    const whole = b.isSame(b.clone().endOf(unit), "day");
    const end = whole ? start.clone().endOf(unit) : b.clone().subtract(1, unit);
    return [start.format(DAY), end.format(DAY)];
  }
  const count = b.diff(a, "days") + 1;
  return [
    a.clone().subtract(count, "days").format(DAY),
    a.clone().subtract(1, "day").format(DAY),
  ];
}

/**
 * The period's dates a year back: the same dates a year earlier, or for a
 * year the previous year, so a year's one choice is Previous year (on Feb 28
 * after a leap year the same dates would end on Feb 29).
 */
export function yearEarlier(period: Period, range: Range): Range {
  return UNIT_OF[period] === "year"
    ? previousPeriod(period, range)
    : sameRangeLastYear(range);
}

/** A choice: its name ("Previous month"), and the name with its dates. */
export type CompareChoice = {
  value: CompareTo;
  name: string;
  label: string;
  range?: Range;
};

/**
 * Compare to's choices for the period, each saying what it compares, with its
 * dates: "Previous month (Sep 1 – 7)", "Same month last year (Oct 1 – 7,
 * 2025)", "Previous 10 days (…)". Two that come to the same dates are one, so
 * a year has only "Previous year". Dates in the period's own year leave the
 * year out.
 */
export function compareChoices(period: Period, range: Range): CompareChoice[] {
  const unit = UNIT_OF[period];
  const year = range[1].slice(0, 4);
  const choice = (value: CompareTo, name: string, dates: Range) => {
    const text = rangeLabel(dates);
    const shown = dates.every((d) => d.startsWith(year))
      ? text.replace(`, ${year}`, "")
      : text;
    return { value, name, label: `${name} (${shown})`, range: dates };
  };
  const days = moment(range[1], DAY).diff(moment(range[0], DAY), "days") + 1;
  const back = previousPeriod(period, range);
  const yearBack = yearEarlier(period, range);
  const repeats = back[0] === yearBack[0] && back[1] === yearBack[1];
  return [
    { value: "none", name: "No comparison", label: "No comparison" },
    choice(
      "previous_period",
      unit
        ? `Previous ${unit}`
        : days === 1
        ? "Previous day"
        : `Previous ${days} days`,
      back
    ),
    ...(repeats
      ? []
      : [
          choice(
            "previous_year",
            // A week last year starts on another weekday: the same dates.
            unit && unit !== "week"
              ? `Same ${unit} last year`
              : "Same dates last year",
            yearBack
          ),
        ]),
    { value: "custom", name: "Custom", label: "Custom" },
  ];
}

// Days come in whole cents and in hours to hundredths; sums of hours are kept
// to hundredths so a group adds up exactly to its days.
const hundredths = (n: number) => Math.round(n * 100) / 100;

/**
 * Days grouped into weeks, 7-day blocks counted from the range's first day
 * (so Sep 1–7 lines up with Aug 1–7), or calendar months cut to the range.
 * Bucket 1 starts on the first picked day. A group has started when its
 * first day has, and finished when its last day has.
 */
export function group(days: CompareBucket[], by: CompareBy): CompareBucket[] {
  if (by === CompareBy.DAY) return days;
  const out: CompareBucket[] = [];
  days.forEach((d, i) => {
    const opens =
      by === CompareBy.WEEK
        ? i % 7 === 0
        : i === 0 || moment(d.start, DAY).date() === 1;
    if (opens) {
      out.push({ ...d });
      return;
    }
    const g = out[out.length - 1];
    g.until = d.until;
    g.revenue += d.revenue;
    g.tutor_pay += d.tutor_pay;
    g.hours = hundredths(g.hours + d.hours);
    g.finished = d.finished;
  });
  return out;
}

/** A measure's value in a bucket: dollars, or hours. */
export function valueOf(bucket: CompareBucket, measure: Measure): number {
  switch (measure) {
    case "revenue":
      return bucket.revenue / 100;
    case "cost":
      return bucket.tutor_pay / 100;
    case "net":
      return (bucket.revenue - bucket.tutor_pay) / 100;
    default:
      return bucket.hours;
  }
}

export type Totals = Record<Measure, number>;

/** Every bucket of a period added up: the summary columns' numbers. */
export function totalsOf(period?: ComparePeriod | null): Totals {
  const totals: Totals = { revenue: 0, cost: 0, net: 0, hours: 0 };
  (period?.buckets ?? []).forEach((bucket) =>
    MEASURES.forEach(({ value }) => {
      totals[value] += valueOf(bucket, value);
    })
  );
  return totals;
}

export type Row = {
  index: number; // 1-based: bucket 1 is the first day, week or month
  current?: CompareBucket;
  compared?: CompareBucket;
};

const daysIn = (buckets: CompareBucket[]) =>
  buckets.length
    ? moment(buckets[buckets.length - 1].until, DAY).diff(
        moment(buckets[0].start, DAY),
        "days"
      ) + 1
    : 0;

/**
 * Bucket i of each period side by side. When both periods have as many days
 * (Previous period for custom dates or the last 6 months) but their months
 * fall so the compared one has more buckets, the extra compared buckets fold
 * into the last row: no row or point past the end, and the compared total is
 * unchanged.
 */
export function alignRows(
  current: CompareBucket[],
  compared?: CompareBucket[] | null
): Row[] {
  if (
    compared &&
    current.length &&
    compared.length > current.length &&
    daysIn(compared) === daysIn(current)
  ) {
    const last = compared.slice(current.length - 1).reduce((g, b) => ({
      ...g,
      until: b.until,
      revenue: g.revenue + b.revenue,
      tutor_pay: g.tutor_pay + b.tutor_pay,
      hours: hundredths(g.hours + b.hours),
      finished: b.finished,
    }));
    compared = [...compared.slice(0, current.length - 1), last];
  }
  const count = Math.max(current.length, compared?.length ?? 0);
  return Array.from({ length: count }, (_, i) => ({
    index: i + 1,
    current: current[i],
    compared: compared?.[i],
  }));
}

/** A row's buckets added up for one side (cents and hours), as a bucket. */
export function sumRows(
  rows: Row[],
  side: "current" | "compared"
): CompareBucket {
  return {
    start: "",
    until: "",
    started: true,
    finished: true,
    revenue: rows.reduce((t, r) => t + (r[side]?.revenue ?? 0), 0),
    tutor_pay: rows.reduce((t, r) => t + (r[side]?.tutor_pay ?? 0), 0),
    hours: rows.reduce((t, r) => hundredths(t + (r[side]?.hours ?? 0)), 0),
  };
}

/**
 * Whether a bucket's values are shown: once it has started, or before then
 * when sessions are already booked in it (the Meetings basis counts them).
 */
export function shows(bucket?: CompareBucket): bucket is CompareBucket {
  return (
    !!bucket &&
    (bucket.started ||
      bucket.revenue !== 0 ||
      bucket.tutor_pay !== 0 ||
      bucket.hours !== 0)
  );
}

/**
 * The bucket holding today: "so far", or on the Meetings basis "so far and
 * booked", since it also counts sessions booked later today. A later bucket
 * shown is "scheduled".
 */
export function tagOf(
  bucket: CompareBucket | undefined,
  basis: Basis
): string | null {
  if (!shows(bucket)) return null;
  if (!bucket.started) return "scheduled";
  if (bucket.finished) return null;
  return basis === Basis.MEETINGS ? "so far and booked" : "so far";
}

export const UNIT: Record<CompareBy, string> = {
  [CompareBy.DAY]: "Day",
  [CompareBy.WEEK]: "Week",
  [CompareBy.MONTH]: "Month",
};

/** "Oct 13 – 19, 2026", "Sep 27 – Oct 3, 2026", "Oct 2026", "Oct 5, 2026". */
export function bucketDates(
  bucket: { start: string; until: string },
  by: CompareBy
): string {
  const a = moment(bucket.start, DAY);
  const b = moment(bucket.until, DAY);
  if (a.isSame(b, "day")) return a.format("MMM D, YYYY");
  if (
    by === CompareBy.MONTH &&
    a.date() === 1 &&
    b.isSame(b.clone().endOf("month"), "day")
  ) {
    return a.format("MMM YYYY");
  }
  if (a.isSame(b, "month")) {
    return `${a.format("MMM D")} – ${b.format("D, YYYY")}`;
  }
  if (a.isSame(b, "year")) {
    return `${a.format("MMM D")} – ${b.format("MMM D, YYYY")}`;
  }
  return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`;
}

export const rangeLabel = ([start, until]: Range) =>
  bucketDates({ start, until }, CompareBy.WEEK);

// Dollars to the cent and hours to two places, exactly what the days hold, so
// a column adds up to its total and a change is the difference shown.
export const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });
export const hoursText = (n: number) =>
  `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })} hrs`;
export const format = (measure: Measure, n: number) =>
  measure === "hours" ? hoursText(n) : money(n);

/**
 * Percent change, or null when the base is under 1 (a dollar or an hour): a
 * percent against nothing, or almost nothing, means nothing.
 */
export function percentChange(now: number, then: number): number | null {
  if (then < 1) return null;
  return ((now - then) / then) * 100;
}

/** "+$1,240.00 · 9.8%": the change in dollars (or hours) and percent. */
export function changeText(measure: Measure, now: number, then: number) {
  const diff = now - then;
  const pct = percentChange(now, then);
  const amount = `${diff >= 0 ? "+" : "−"}${format(measure, Math.abs(diff))}`;
  return pct === null ? amount : `${amount} · ${Math.abs(pct).toFixed(1)}%`;
}

/** The table on screen as CSV: one row per bucket, then the total. */
export function toCsv(
  rows: Row[],
  by: CompareBy,
  withAgainst: boolean,
  basis: Basis
): string {
  const unit = UNIT[by];
  const head = [unit, "Dates"];
  if (withAgainst) head.push("Compared dates");
  MEASURES.forEach((m) => {
    head.push(m.label);
    if (withAgainst) {
      head.push(`Compared ${m.label.toLowerCase()}`, `${m.label} change %`);
    }
  });
  head.push("Status");

  const values = (now: CompareBucket | null, then: CompareBucket | null) =>
    MEASURES.flatMap((m) => {
      const a = now ? valueOf(now, m.value) : null;
      const b = then ? valueOf(then, m.value) : null;
      const cells = [a === null ? "" : a.toFixed(2)];
      if (withAgainst) {
        // As on screen: the value shown less the compared value shown.
        const pct = a !== null && b !== null ? percentChange(a, b) : null;
        cells.push(
          b === null ? "" : b.toFixed(2),
          pct === null ? "" : pct.toFixed(1)
        );
      }
      return cells;
    });
  const dates = (b?: { start: string; until: string }) =>
    b ? `${b.start} to ${b.until}` : "";
  const shown = (b?: CompareBucket) => (shows(b) ? b : null);

  const lines = rows.map((row) => {
    const { current, compared } = row;
    const status = shows(current) ? tagOf(current, basis) ?? "" : "not started";
    const line = [`${unit} ${row.index}`, dates(current)];
    if (withAgainst) line.push(dates(compared));
    return [...line, ...values(shown(current), shown(compared)), status];
  });
  const total = ["Total", ""];
  if (withAgainst) total.push("");
  lines.push([
    ...total,
    ...values(
      sumRows(rows, "current"),
      withAgainst ? sumRows(rows, "compared") : null
    ),
    "",
  ]);
  const quote = (v: string) =>
    /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  return (
    [head, ...lines].map((line) => line.map(quote).join(",")).join("\n") + "\n"
  );
}
